import { isActiveMember } from './membership';
import {
  PERIOD_LABEL,
  WEEKDAY_LONG,
  diffDays,
  formatDayMonth,
  hashString,
  monthKey,
  periodOf,
  weekdayOf,
} from './dates';
import {
  experienceIndex,
  experienceText,
  isTrainee,
  mentorOf,
  alternateConflict,
  pairConflict,
  togetherPartnerIn,
  togetherPartners,
  yearsOfMembership,
  yearsText,
} from './roleRules';
import { availIndex, blackoutOn, isEnabledIn, participantsOf, quantityOf, sexAllowed } from './schedule';
import { isOpenSwap } from './swaps';
import type { AvailState, CoupleMode, Member, Ministry, Period, Role, RoleSex, ScheduleEvent } from './types';

/**
 * Geração automática de escala — heurística gulosa que roda no navegador.
 *
 * É a referência de comportamento do protótipo, fiel às regras do modelo de otimização
 * do handoff (`solver/model.md`): as restrições obrigatórias nunca são violadas e os
 * critérios de otimização viram pontuação. Quando a otimização real (OR-Tools no
 * servidor) existir, ela substitui esta função sem mudar a interface: a entrada
 * (`GenerationOptions`) e a saída (`GenerationResult`) são o contrato.
 */

export interface GenerationCriteria {
  /** Distribui as escalas de forma uniforme. */
  balance: boolean;
  /** Evita a mesma pessoa em semanas seguidas. */
  rotation: boolean;
  /** Não escala a pessoa duas vezes no mesmo dia. */
  repeat: boolean;
  /** Escala juntos, quando possível, os casais que escolheram "Servir juntos". */
  couples: boolean;
  /** Prioriza quem confirmou disponibilidade. */
  individual: boolean;
  /** Considera as participações das últimas 4 semanas. */
  history: boolean;
}

export const DEFAULT_CRITERIA: GenerationCriteria = {
  balance: true,
  rotation: true,
  repeat: true,
  couples: true,
  individual: true,
  history: true,
};

export interface GenerationOptions {
  ministryIds: readonly string[];
  disabledRoleIds: readonly string[];
  /** Máximo de escalas por membro no mês. */
  limit: number;
  /** Aceita quem não configurou a disponibilidade. */
  allowUnset: boolean;
  criteria: GenerationCriteria;
  /** Simulação "e se" aplicada: membros fora desta geração. */
  excludedMemberIds?: readonly string[];
  /**
   * Simulação "e se" aplicada: quantidade por função (roleId → quantidade) em todos os
   * eventos gerados. Fica gravada no evento (`need`) quando difere da padrão.
   */
  needOverrides?: Readonly<Record<string, number>>;
}

/** Por que cada pessoa foi escolhida — alimenta a matriz de decisão. */
export interface Decision {
  eventId: string;
  roleId: string;
  ministryId: string;
  memberId: string;
  availability: AvailState;
  weekday: number;
  period: Period;
  /** Escalas do membro no mês antes desta escolha. */
  load: number;
  /** Menor carga entre os candidatos. */
  minLoad: number;
  /** Dias até a escala mais próxima do membro, ou `null` sem outra escala. */
  gapDays: number | null;
  withSpouse: boolean;
  /** Escala do casal da pessoa (`null` sem cônjuge vinculado ou sem preferência). */
  coupleMode: CoupleMode | null;
  /** O parceiro de "servem juntos" que ficou na mesma função do evento (`null` sem ele). */
  partnerId: string | null;
  candidates: number;
  nextId: string | null;
  nextLoad: number | null;
  excluded: {
    used: number;
    sex: number;
    blackout: number;
    unavailable: number;
    limit: number;
    /**
     * Fora pelas regras avançadas da função (par, treinamento, experiência, tempo de casa)
     * ou pelo revezamento do casal.
     */
    rules: number;
  };
  allowedSex: RoleSex;
  enabledCount: number;
  /** Escalas anteriores da pessoa nesta função. */
  experience: number;
  /** Em treinamento: quem a acompanha na função (`null` fora do treinamento). */
  mentorId: string | null;
  critical: boolean;
}

export interface PendingSlot {
  eventId: string;
  roleId: string;
  ministryId: string;
  missing: number;
  reason: string;
  /** Função crítica: a interface marca a vaga com "Crítica ·". */
  critical: boolean;
}

export interface GenerationResult {
  /** Todos os eventos, com os gerados preenchidos (ainda não aplicados ao estado). */
  events: ScheduleEvent[];
  eventIds: string[];
  filled: number;
  needed: number;
  /** Posições das funções críticas: preenchidas e necessárias. */
  criticalFilled: number;
  criticalNeeded: number;
  pending: PendingSlot[];
  /** Quantas escolhas tinham disponibilidade confirmada. */
  preferenceHits: number;
  /** Casais com "Servir juntos" escalados no mesmo evento. */
  couples: number;
  /** Pares que servem juntos (regra da função) escalados juntos na mesma função do evento. */
  pairs: number;
  /** Escalas atribuídas nesta geração, por membro. */
  load: Record<string, number>;
  decisions: Decision[];
}

const RECENT_DAYS = 28;
const REPAIR_DEPTH = 3;
/**
 * Quanto completar um par que serve junto pesa na escolha: vale mais que uma escala a mais
 * no mês (o equilíbrio pesa 10 por escala), para o par não se separar por pouco.
 */
const PAIR_BONUS = 12;

interface Seat {
  ministryId: string;
  role: Role;
}

interface Occasion {
  event: ScheduleEvent;
  weekday: number;
  period: Period;
  slot: number;
  roles: Role[];
  seats: Seat[];
  /** Quem vai assumir, por pedido de troca em aberto, uma função que esta geração não toca. */
  promised: Set<string>;
}

type Gap = Omit<PendingSlot, 'reason'>;

export function generateSchedule(
  allEvents: readonly ScheduleEvent[],
  eventIds: readonly string[],
  members: readonly Member[],
  ministries: readonly Ministry[],
  options: GenerationOptions
): GenerationResult {
  const targets = new Set(eventIds);
  const events = allEvents.map((e) => (targets.has(e.id) ? { ...e, slots: { ...e.slots } } : e));
  const ministryById = new Map(ministries.map((m) => [m.id, m]));
  const criteria = options.criteria;
  const excluded = new Set(options.excludedMemberIds ?? []);
  const overrides = options.needOverrides ?? {};
  // Inativo não entra na geração.
  const pool = members.filter((m) => !excluded.has(m.id) && isActiveMember(m));

  // Histórico real: quem faltou não conta e quem cobriu alguém conta.
  const served = new Map<string, string[]>();
  const monthLoad = new Map<string, number>();
  const shift = (memberId: string, date: string, delta: 1 | -1) => {
    const dates = served.get(memberId) ?? [];
    const i = dates.lastIndexOf(date);

    served.set(memberId, delta > 0 ? [...dates, date] : dates.filter((_, k) => k !== i));
    const key = `${memberId}|${monthKey(date)}`;
    monthLoad.set(key, (monthLoad.get(key) ?? 0) + delta);
  };
  const loadOf = (memberId: string, date: string) => monthLoad.get(`${memberId}|${monthKey(date)}`) ?? 0;

  for (const e of events) {
    if (!targets.has(e.id)) participantsOf(e).forEach((id) => shift(id, e.date, 1));
  }

  const experience = experienceIndex(events.filter((e) => !targets.has(e.id)));

  const isSelected = (ministryId: string, roleId: string) =>
    options.ministryIds.includes(ministryId) && !options.disabledRoleIds.includes(roleId);

  const decisions: Decision[] = [];
  const load: Record<string, number> = {};
  let filled = 0;
  let needed = 0;
  let criticalFilled = 0;
  let criticalNeeded = 0;
  let preferenceHits = 0;

  const inEventOf = (event: ScheduleEvent) => new Set(Object.values(event.slots).flat());
  // Quem prometeu assumir a função de outro (troca em aberto fora da geração) não é
  // escalado em outra: senão a troca, que não é desta geração, seria cancelada.
  const takenIn = (o: Occasion, event = o.event) => new Set([...inEventOf(event), ...o.promised]);
  const enabledIn = (seat: Seat) => pool.filter((m) => isEnabledIn(m, seat.ministryId, seat.role.id));

  /** `freed`: escalas do mês que a troca devolve à pessoa (a que ela deixa). */
  const fits = (o: Occasion, m: Member, role: Role, event = o.event, freed = 0) => {
    const a = m.availability[o.slot] ?? 'unset';

    return (
      sexAllowed(role, m) &&
      !blackoutOn(m, o.event.date) &&
      // Quem avisou "Não posso ir" neste evento não volta pela geração.
      o.event.responses[m.id]?.status !== 'declined' &&
      (a === 'available' || (a === 'unset' && options.allowUnset)) &&
      loadOf(m.id, o.event.date) - freed < options.limit &&
      !ruleBlock(m, { event, role, roles: o.roles, experience })
    );
  };

  const gapOf = (o: Occasion, memberId: string): number | null => {
    const dates = served.get(memberId) ?? [];

    return dates.length ? Math.min(...dates.map((d) => Math.abs(diffDays(d, o.event.date)))) : null;
  };
  const recentOf = (o: Occasion, memberId: string) =>
    (served.get(memberId) ?? []).filter((d) => {
      const n = diffDays(d, o.event.date);

      return n > 0 && n <= RECENT_DAYS;
    }).length;

  /** `seat` e `event`: a vaga em disputa — quem completa um par que serve junto sobe na fila. */
  const score = (o: Occasion, m: Member, seat?: Seat, event = o.event) => {
    const a = m.availability[o.slot] ?? 'unset';
    const gap = gapOf(o, m.id);
    let v = loadOf(m.id, o.event.date) * (criteria.balance ? 10 : 2);

    if (a === 'unset') v += criteria.individual ? 14 : 4;
    if (criteria.rotation && gap !== null && gap < 7) v += 9;
    if (criteria.repeat && gap === 0) v += 6;
    if (criteria.couples && servesTogether(m) && inEventOf(o.event).has(m.spouseId!)) v -= 7;
    if (criteria.history) v += recentOf(o, m.id) * 0.8;
    if (seat && togetherPartnerIn(event, seat.role, m.id)) v -= PAIR_BONUS;

    // Desempate estável: a mesma entrada gera sempre a mesma escala.
    return v + (hashString(m.id + o.event.id) % 7) * 0.05;
  };

  const candidatesFor = (o: Occasion, seat: Seat, event = o.event) => {
    const present = takenIn(o, event);

    return enabledIn(seat)
      .filter((m) => !present.has(m.id) && fits(o, m, seat.role, event))
      .sort((a, b) => score(o, a, seat, event) - score(o, b, seat, event));
  };

  const place = (o: Occasion, seat: Seat, pick: Member) => {
    const { event } = o;
    const { role, ministryId } = seat;
    const candidates = [pick, ...candidatesFor(o, seat).filter((m) => m.id !== pick.id)];
    const enabled = enabledIn(seat);
    const next = candidates[1];
    const a = pick.availability[o.slot] ?? 'unset';
    const before = takenIn(o);

    before.delete(pick.id);
    decisions.push({
      eventId: event.id,
      roleId: role.id,
      ministryId,
      memberId: pick.id,
      availability: a,
      weekday: o.weekday,
      period: o.period,
      load: loadOf(pick.id, event.date),
      minLoad: Math.min(...candidates.map((c) => loadOf(c.id, event.date))),
      gapDays: gapOf(o, pick.id),
      withSpouse: false,
      coupleMode: pick.spouseId ? pick.coupleMode : null,
      partnerId: null,
      candidates: candidates.length,
      nextId: next?.id ?? null,
      nextLoad: next ? loadOf(next.id, event.date) : null,
      excluded: excludedCounts(
        enabled,
        before,
        { event, role, roles: o.roles, experience },
        o.slot,
        options,
        loadOf
      ),
      allowedSex: role.allowedSex,
      enabledCount: enabled.length,
      experience: experience.count(pick.id, role.id, event.date),
      mentorId: isTrainee(role, pick.id) ? mentorOf(event, role, pick.id) : null,
      critical: role.critical,
    });

    event.slots[role.id] = [...(event.slots[role.id] ?? []), pick.id];
    shift(pick.id, event.date, 1);
    experience.add(pick.id, role.id, event.date);
    load[pick.id] = (load[pick.id] ?? 0) + 1;
    filled++;
    if (role.critical) criticalFilled++;
    if (a === 'available') preferenceHits++;
  };

  const unplace = (o: Occasion, seat: Seat, memberId: string) => {
    const { event } = o;
    const { role } = seat;
    const m = pool.find((x) => x.id === memberId)!;

    event.slots[role.id] = (event.slots[role.id] ?? []).filter((id) => id !== memberId);
    shift(memberId, event.date, -1);
    experience.remove(memberId, role.id, event.date);
    load[memberId] = (load[memberId] ?? 1) - 1;
    if (!load[memberId]) delete load[memberId];
    filled--;
    if (role.critical) criticalFilled--;
    if ((m.availability[o.slot] ?? 'unset') === 'available') preferenceHits--;

    const i = decisions.findIndex((d) => d.eventId === event.id && d.memberId === memberId);

    if (i >= 0) decisions.splice(i, 1);
  };

  const prepare = (event: ScheduleEvent): Occasion => {
    const weekday = weekdayOf(event.date);
    const period = periodOf(event.time);
    const all = event.ministryIds.flatMap((ministryId) =>
      (ministryById.get(ministryId)?.roles ?? []).map((role): Seat => ({ ministryId, role }))
    );

    // Limpa as funções que vão ser geradas; quem está nas demais continua ocupado.
    for (const { ministryId, role } of all) {
      if (!isSelected(ministryId, role.id)) continue;

      event.slots[role.id] = [];

      // A quantidade simulada fica gravada no evento.
      const override = overrides[role.id];

      if (override !== undefined) {
        const need = { ...event.need };

        if (override === role.qty) delete need[role.id];
        else need[role.id] = override;

        event.need = need;
      }
    }

    event.generated = true;
    event.published = false;

    return {
      event,
      weekday,
      period,
      slot: availIndex(weekday, period),
      roles: all.map((x) => x.role),
      seats: all.filter(
        ({ ministryId, role }) => isSelected(ministryId, role.id) && quantityOf(event, role) > 0
      ),
      promised: new Set(
        event.swaps
          .filter(
            (s) =>
              isOpenSwap(s) && !all.some((x) => x.role.id === s.roleId && isSelected(x.ministryId, s.roleId))
          )
          .map((s) => s.toId)
      ),
    };
  };

  const fill = (o: Occasion): Gap[] => {
    const gaps: Gap[] = [];
    // As funções críticas são preenchidas antes: faltando gente, a vaga fica nas outras.
    const queue = [...o.seats].sort((a, b) => Number(b.role.critical) - Number(a.role.critical));

    for (const seat of queue) {
      const qty = quantityOf(o.event, seat.role);

      needed += qty;
      if (seat.role.critical) criticalNeeded += qty;

      for (let k = 0; k < qty;) {
        const pick = candidatesFor(o, seat)[0];

        if (!pick) {
          gaps.push({
            eventId: o.event.id,
            roleId: seat.role.id,
            ministryId: seat.ministryId,
            missing: qty - k,
            critical: seat.role.critical,
          });
          break;
        }

        place(o, seat, pick);
        k++;

        // Pares que servem juntos: sobrando vaga, o parceiro entra logo depois, se puder.
        for (const partnerId of togetherPartners(seat.role, pick.id)) {
          if (k >= qty) break;

          const partner = candidatesFor(o, seat).find((m) => m.id === partnerId);

          if (partner) {
            place(o, seat, partner);
            k++;
          }
        }
      }
    }

    return gaps;
  };

  type Step = { memberId: string; from: Seat | null; to: Seat };

  // A cadeia só vale quando a última vaga dela fica com alguém livre.
  const chainIn = (
    o: Occasion,
    to: Seat,
    event: ScheduleEvent,
    visited: Set<string>,
    depth: number
  ): Step[] | null => {
    const fresh = candidatesFor(o, to, event)[0];

    if (fresh) return [{ memberId: fresh.id, from: null, to }];
    if (depth === 0) return null;

    for (const m of enabledIn(to)) {
      const from = o.seats.find(
        (x) => !visited.has(x.role.id) && (event.slots[x.role.id] ?? []).includes(m.id)
      );

      if (!from) continue;

      const without = withoutMember(event, m.id, from.role.id);

      if (!fits(o, m, to.role, without, 1) || leavesTraineeAlone(without, from.role)) continue;

      const moved = {
        ...without,
        slots: { ...without.slots, [to.role.id]: [...(without.slots[to.role.id] ?? []), m.id] },
      };
      const rest = chainIn(o, from, moved, new Set([...visited, from.role.id]), depth - 1);

      if (rest) return [{ memberId: m.id, from, to }, ...rest];
    }

    return null;
  };

  const chain = (o: Occasion, seat: Seat): boolean => {
    const path = chainIn(o, seat, o.event, new Set([seat.role.id]), REPAIR_DEPTH);

    for (const step of path ?? []) {
      if (step.from) unplace(o, step.from, step.memberId);
      place(
        o,
        step.to,
        pool.find((m) => m.id === step.memberId)!
      );
    }

    return !!path;
  };

  const occasions = events
    .filter((e) => targets.has(e.id))
    .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time))
    .map(prepare);

  const release = (o: Occasion, seat: Seat): boolean => {
    const present = takenIn(o);
    const capped = enabledIn(seat)
      .filter(
        (m) =>
          !present.has(m.id) &&
          loadOf(m.id, o.event.date) >= options.limit &&
          fits(o, m, seat.role, o.event, 1)
      )
      .sort((a, b) => score(o, a) - score(o, b));

    for (const m of capped) {
      for (const other of occasions) {
        if (other === o || monthKey(other.event.date) !== monthKey(o.event.date)) continue;

        const from = other.seats.find((x) => (other.event.slots[x.role.id] ?? []).includes(m.id));

        if (!from) continue;

        const without = withoutMember(other.event, m.id, from.role.id);

        if (leavesTraineeAlone(without, from.role)) continue;

        const substitute = candidatesFor(other, from, without).find((x) => x.id !== m.id);

        if (!substitute) continue;

        unplace(other, from, m.id);
        place(other, from, substitute);
        place(o, seat, m);

        return true;
      }
    }

    return false;
  };

  const gaps: Gap[] = [];

  for (const o of occasions) {
    const own = fill(o);

    for (const gap of own) {
      const seat = o.seats.find((x) => x.role.id === gap.roleId)!;

      while (gap.missing > 0 && chain(o, seat)) gap.missing--;
    }

    gaps.push(...own);
  }

  const occasionOf = new Map(occasions.map((o) => [o.event.id, o]));

  for (const gap of gaps) {
    const o = occasionOf.get(gap.eventId)!;
    const seat = o.seats.find((x) => x.role.id === gap.roleId)!;

    while (gap.missing > 0 && (chain(o, seat) || release(o, seat))) gap.missing--;
  }

  const pending: PendingSlot[] = gaps
    .filter((gap) => gap.missing > 0)
    .map((gap) => {
      const o = occasionOf.get(gap.eventId)!;
      const seat = o.seats.find((x) => x.role.id === gap.roleId)!;
      const ctx = { event: o.event, role: seat.role, roles: o.roles, experience };

      return {
        ...gap,
        reason: pendingReason(enabledIn(seat), takenIn(o), ctx, o.slot, options, loadOf),
      };
    });

  // Cônjuge escalado depois também conta: confere com os eventos completos.
  const couplePairs = new Set<string>();
  const order = new Map(occasions.map((o, i) => [o.event.id, i]));

  decisions.sort((a, b) => order.get(a.eventId)! - order.get(b.eventId)!);

  for (const decision of decisions) {
    const present = inEventOf(occasionOf.get(decision.eventId)!.event);
    const picked = members.find((m) => m.id === decision.memberId);
    const spouseId = picked?.spouseId;

    decision.withSpouse = !!spouseId && present.has(spouseId);

    if (decision.withSpouse && spouseId && picked && servesTogether(picked)) {
      couplePairs.add([decision.memberId, spouseId].sort().join('|'));
    }
  }

  const linked = linkPartners(decisions, events, ministries);

  return {
    events,
    eventIds: [...eventIds],
    filled,
    needed,
    criticalFilled,
    criticalNeeded,
    pending,
    preferenceHits,
    couples: couplePairs.size,
    pairs: linked.pairs,
    load,
    decisions: linked.decisions,
  };
}

/**
 * Liga cada decisão ao parceiro de "servem juntos" que ficou na mesma função do evento e
 * conta os pares (cada um uma vez), com os eventos como estão — a proposta ou a ajustada.
 */
function linkPartners(
  decisions: readonly Decision[],
  events: readonly ScheduleEvent[],
  ministries: readonly Ministry[]
): { decisions: Decision[]; pairs: number } {
  const eventOf = new Map(events.map((e) => [e.id, e]));
  const roleOf = new Map(ministries.flatMap((m) => m.roles.map((r) => [r.id, r] as const)));
  const pairs = new Set<string>();

  const linked = decisions.map((d) => {
    const event = eventOf.get(d.eventId);
    const role = roleOf.get(d.roleId);
    const partnerId = event && role ? togetherPartnerIn(event, role, d.memberId) : null;

    if (partnerId) pairs.add([d.eventId, d.roleId, ...[d.memberId, partnerId].sort()].join('|'));

    return d.partnerId === partnerId ? d : { ...d, partnerId };
  });

  return { decisions: linked, pairs: pairs.size };
}

interface RuleContext {
  event: ScheduleEvent;
  role: Role;
  /** Todas as funções do evento (os pares de outras funções também valem). */
  roles: readonly Role[];
  experience: ReturnType<typeof experienceIndex>;
}

type RuleBlock = 'pair' | 'couple' | 'trainee' | 'experience' | 'years';

/** O casal escolheu "Servir juntos" (o critério "Preferência por casais" só vale para ele). */
function servesTogether(member: Member): boolean {
  return !!member.spouseId && member.coupleMode === 'together';
}

/**
 * A regra que tira a pessoa da vaga, se alguma tira: o revezamento do casal (obrigatório:
 * com o cônjuge no evento, em qualquer função, a pessoa fica de fora) ou uma regra
 * avançada da função.
 */
function ruleBlock(member: Member, { event, role, roles, experience }: RuleContext): RuleBlock | null {
  if (pairConflict(event, member.id, role, roles)) return 'pair';
  if (alternateConflict(event, member)) return 'couple';

  if (isTrainee(role, member.id)) {
    // Em treinamento: só junto de alguém experiente já escalado na função.
    if (!mentorOf(event, role, member.id)) return 'trainee';
  } else if (
    role.minExperience > 0 &&
    experience.count(member.id, role.id, event.date) < role.minExperience
  ) {
    return 'experience';
  }

  if (role.minYears > 0) {
    const years = yearsOfMembership(member.memberSince, event.date);

    if (years === null || years < role.minYears) return 'years';
  }

  return null;
}

function pendingReason(
  enabled: Member[],
  used: Set<string>,
  ctx: RuleContext,
  slot: number,
  options: GenerationOptions,
  loadOf: (memberId: string, date: string) => number
): string {
  const { event, role } = ctx;
  const limit = options.limit;

  if (!enabled.length) {
    return options.excludedMemberIds?.length
      ? 'Nenhum membro habilitado para esta função fora dos tirados na simulação.'
      : 'Nenhum membro habilitado para esta função.';
  }

  const free = enabled.filter((m) => m.availability[slot] !== 'unavailable' && !blackoutOn(m, event.date));

  if (!free.length) {
    const p = periodOf(event.time);

    return `Nenhum membro habilitado está disponível (${WEEKDAY_LONG[weekdayOf(event.date)]!.toLowerCase()} · ${PERIOD_LABEL[p].toLowerCase()}).`;
  }

  if (free.every((m) => used.has(m.id))) return 'Os membros disponíveis já estão escalados neste evento.';

  const open = free.filter((m) => !used.has(m.id) && loadOf(m.id, event.date) < limit);

  if (!open.length) return `Os membros disponíveis atingiram o limite de ${limit} escalas no mês.`;

  const present = open.filter((m) => event.responses[m.id]?.status !== 'declined');

  if (!present.length) return 'Os membros disponíveis avisaram que não podem ir a este evento.';

  const allowed = present.filter((m) => sexAllowed(role, m));

  if (!allowed.length)
    return `${role.name} aceita apenas ${role.allowedSex === 'F' ? 'mulheres' : 'homens'}, e os membros disponíveis não são.`;

  const confirmed = allowed.filter((m) => {
    const a = m.availability[slot] ?? 'unset';

    return a === 'available' || (a === 'unset' && options.allowUnset);
  });

  if (!confirmed.length) return 'Os membros disponíveis ainda não configuraram a disponibilidade.';

  // Quem sobrou e não entrou: explica pela regra avançada mais comum entre eles.
  const blocks = confirmed.map((m) => ruleBlock(m, ctx)).filter((b): b is RuleBlock => b !== null);

  if (blocks.length === confirmed.length) {
    const count = (kind: RuleBlock) => blocks.filter((b) => b === kind).length;
    const top = (['experience', 'years', 'trainee', 'pair', 'couple'] as const).reduce((a, b) =>
      count(b) > count(a) ? b : a
    );

    return RULE_REASON[top](role);
  }

  return 'Há membros livres que podem ocupar a vaga: escale à mão.';
}

const RULE_REASON: Record<RuleBlock, (role: Role) => string> = {
  experience: (role) =>
    `Os membros disponíveis não têm a experiência mínima da função (${experienceText(role.minExperience)}).`,
  years: (role) =>
    `Os membros disponíveis não têm o tempo de casa mínimo da função (${yearsText(role.minYears)}).`,
  trainee: () =>
    'Os membros disponíveis estão em treinamento e não há alguém experiente na função para acompanhá-los.',
  pair: () => 'Os membros disponíveis formam par restrito com alguém já escalado neste evento.',
  couple: () => 'Os membros disponíveis revezam os eventos com o cônjuge, já escalado neste evento.',
};

function excludedCounts(
  enabled: Member[],
  used: Set<string>,
  ctx: RuleContext,
  slot: number,
  options: GenerationOptions,
  loadOf: (memberId: string, date: string) => number
): Decision['excluded'] {
  const ex = { used: 0, sex: 0, blackout: 0, unavailable: 0, limit: 0, rules: 0 };
  const { event, role } = ctx;

  for (const m of enabled) {
    const a = m.availability[slot] ?? 'unset';

    if (used.has(m.id)) ex.used++;
    else if (role.allowedSex !== 'any' && role.allowedSex !== m.sex) ex.sex++;
    else if (blackoutOn(m, event.date)) ex.blackout++;
    else if (!(a === 'available' || (a === 'unset' && options.allowUnset))) ex.unavailable++;
    else if (loadOf(m.id, event.date) >= options.limit) ex.limit++;
    else if (ruleBlock(m, ctx)) ex.rules++;
  }

  return ex;
}

/** Nível de equilíbrio (0–100): quanto menor a dispersão da carga, maior. */
export function balanceScore(load: Record<string, number>, eligibleIds: readonly string[]): number {
  const values = eligibleIds.map((id) => load[id] ?? 0);
  const mean = values.reduce((a, b) => a + b, 0) / Math.max(1, values.length);

  if (!mean) return 0;

  const sd = Math.sqrt(values.reduce((a, b) => a + (b - mean) ** 2, 0) / Math.max(1, values.length));

  return Math.max(0, Math.round((1 - sd / mean / 1.4) * 100));
}

/** Por que um membro habilitado não ocupou a vaga pendente. */
export type PendingBlocker =
  | 'excluded'
  | 'declined'
  | 'scheduled'
  | 'assigned'
  | 'swap'
  | 'sex'
  | 'blackout'
  | 'unavailable'
  | 'unset'
  | 'limit'
  | RuleBlock
  /** Nada impede agora (a proposta mudou desde a escolha): dá para escalar à mão. */
  | 'free';

export type PendingAction = { kind: 'move'; fromRoleId: string } | { kind: 'assign' };

export interface PendingCandidate {
  memberId: string;
  blocker: PendingBlocker;
  /** Rótulo curto ("Já escalado", "Indisponível"…). */
  title: string;
  /** A frase com o detalhe, já com os nomes. */
  detail: string;
  action: PendingAction | null;
}

export interface PendingExplanation {
  /** Quantos membros estão habilitados na função. */
  enabledCount: number;
  /** Dia e período do evento ("domingo · manhã"). */
  when: string;
  /** Cada habilitado e o que o tirou da vaga, na ordem em que o gerador confere. */
  candidates: PendingCandidate[];
  /** O que o administrador ou o líder pode fazer para preencher. */
  suggestions: string[];
}

const BLOCKER_TITLE: Record<PendingBlocker, string> = {
  excluded: 'Fora da simulação',
  declined: 'Avisou que não pode ir',
  scheduled: 'Já escalado no evento',
  assigned: 'Já está nesta função',
  swap: 'Troca em andamento',
  sex: 'Sexo não permitido',
  blackout: 'Ausência programada',
  unavailable: 'Indisponível',
  unset: 'Disponibilidade não configurada',
  limit: 'Limite do mês',
  pair: 'Par restrito',
  couple: 'Revezamento do casal',
  trainee: 'Em treinamento',
  experience: 'Pouca experiência',
  years: 'Pouco tempo de casa',
  free: 'Pode ser escalado(a)',
};

type ExplainOptions = Pick<GenerationOptions, 'limit' | 'allowUnset' | 'excludedMemberIds'> &
  Partial<Pick<GenerationOptions, 'ministryIds' | 'disabledRoleIds'>>;

function isGenerated(options: ExplainOptions, ministryId: string, roleId: string): boolean {
  return !!options.ministryIds?.includes(ministryId) && !options.disabledRoleIds?.includes(roleId);
}

function withoutMember(event: ScheduleEvent, memberId: string, roleId: string): ScheduleEvent {
  return {
    ...event,
    slots: { ...event.slots, [roleId]: (event.slots[roleId] ?? []).filter((id) => id !== memberId) },
  };
}

function leavesTraineeAlone(event: ScheduleEvent, from: Role): boolean {
  return (event.slots[from.id] ?? []).some((id) => isTrainee(from, id) && !mentorOf(event, from, id));
}

/**
 * A explicação detalhada de uma vaga pendente da geração ("Funções pendentes" › mais
 * informações): para cada membro habilitado na função, a regra obrigatória que o tirou —
 * conferidas na mesma ordem do gerador — e, no fim, o que dá para fazer. Usa todos os
 * eventos, já com a proposta (`proposalEvents`): o limite do mês e a experiência contam as
 * outras datas, e quem aparece como "já escalado" é quem ficou em outra função do evento.
 */
export function explainPending(
  pending: Pick<PendingSlot, 'eventId' | 'roleId' | 'ministryId'>,
  events: readonly ScheduleEvent[],
  members: readonly Member[],
  ministries: readonly Ministry[],
  options: ExplainOptions
): PendingExplanation | null {
  const event = events.find((e) => e.id === pending.eventId);
  const ministry = ministries.find((m) => m.id === pending.ministryId);
  const role = ministry?.roles.find((r) => r.id === pending.roleId);

  if (!event || !ministry || !role) return null;

  const weekday = weekdayOf(event.date);
  const period = periodOf(event.time);
  const slot = availIndex(weekday, period);
  const when = `${WEEKDAY_LONG[weekday]!.toLowerCase()} · ${PERIOD_LABEL[period].toLowerCase()}`;
  const roles = event.ministryIds.flatMap((id) => ministries.find((m) => m.id === id)?.roles ?? []);
  const roleOf = new Map(roles.map((r) => [r.id, r]));
  const ministryOf = new Map(
    event.ministryIds.flatMap((id) =>
      (ministries.find((m) => m.id === id)?.roles ?? []).map((r) => [r.id, id] as const)
    )
  );
  const placedIn = new Map<string, Role>();

  for (const [roleId, ids] of Object.entries(event.slots)) {
    const placed = roleOf.get(roleId);

    if (placed && roleId !== role.id) ids.forEach((id) => placedIn.set(id, placed));
  }

  const inRole = new Set(event.slots[role.id] ?? []);
  // A troca em aberto numa função fora desta geração: o colega vai assumi-la.
  const promised = new Map(
    event.swaps
      .filter((s) => isOpenSwap(s) && !isGenerated(options, ministryOf.get(s.roleId) ?? '', s.roleId))
      .map((s) => [s.toId, s] as const)
  );
  // Quem está numa função do evento que esta geração não toca, e o nome dessa função.
  const outside = new Map<string, string>();

  const month = monthKey(event.date);
  const monthLoad = (memberId: string) =>
    events.filter((e) => monthKey(e.date) === month && participantsOf(e).has(memberId)).length;
  const excluded = new Set(options.excludedMemberIds ?? []);
  const ctx: RuleContext = { event, role, roles, experience: experienceIndex(events) };
  const nameOf = (id: string) => members.find((m) => m.id === id)?.name ?? 'outra pessoa';
  const enabled = members.filter((m) => isActiveMember(m) && isEnabledIn(m, ministry.id, role.id));

  const candidates = enabled.map((m): PendingCandidate => {
    const a = m.availability[slot] ?? 'unset';
    const blackout = blackoutOn(m, event.date);
    const say = (
      blocker: PendingBlocker,
      detail: string,
      action: PendingAction | null = null
    ): PendingCandidate => ({
      memberId: m.id,
      blocker,
      title: BLOCKER_TITLE[blocker],
      detail,
      action,
    });

    if (excluded.has(m.id)) return say('excluded', 'Foi tirado(a) desta geração na simulação "e se".');
    if (event.responses[m.id]?.status === 'declined')
      return say('declined', 'Avisou que não pode ir a este evento; a geração não o(a) escala de volta.');

    if (inRole.has(m.id)) return say('assigned', `Já está em ${role.name} neste evento.`);

    const other = placedIn.get(m.id);

    if (other) {
      const first =
        other.critical && !role.critical
          ? ` ${other.name} é função crítica e foi preenchida antes de ${role.name}.`
          : '';

      const generated = isGenerated(options, ministryOf.get(other.id) ?? '', other.id);

      if (!generated) {
        outside.set(m.id, other.name);

        return say(
          'scheduled',
          `Já está em ${other.name} neste evento, que fica fora desta geração: a pessoa continua lá.${first}`
        );
      }

      const after = withoutMember(event, m.id, other.id);
      const movable =
        sexAllowed(role, m) &&
        !blackout &&
        (a === 'available' || (a === 'unset' && options.allowUnset)) &&
        !ruleBlock(m, { ...ctx, event: after }) &&
        !leavesTraineeAlone(after, other);

      return say(
        'scheduled',
        `Já está em ${other.name} neste evento, e cada pessoa ocupa uma função por evento.${first}`,
        movable ? { kind: 'move', fromRoleId: other.id } : null
      );
    }

    const swap = promised.get(m.id);

    if (swap)
      return say(
        'swap',
        `Vai assumir ${roleOf.get(swap.roleId)?.name ?? 'outra função'} no lugar de ${nameOf(swap.fromId)}, pelo pedido de troca em aberto.`
      );

    if (!sexAllowed(role, m))
      return say('sex', `${role.name} aceita apenas ${role.allowedSex === 'F' ? 'mulheres' : 'homens'}.`);
    if (blackout)
      return say(
        'blackout',
        `${blackout.reason || 'Ausência programada'} de ${formatDayMonth(blackout.from)} a ${formatDayMonth(blackout.to)}.`
      );
    if (a === 'unavailable') return say('unavailable', `Marcou que não pode ${when}.`);
    if (a === 'unset' && !options.allowUnset)
      return say(
        'unset',
        `Não configurou a disponibilidade de ${when}, e a geração só aceita quem confirmou.`
      );

    const load = monthLoad(m.id);

    if (load >= options.limit)
      return say('limit', `Já tem ${load} escalas no mês (limite de ${options.limit} por membro).`);

    const block = ruleBlock(m, ctx);
    const spouse = m.spouseId ? nameOf(m.spouseId) : '';
    const RULE_DETAIL: Record<RuleBlock, string> = {
      pair: 'Forma par restrito com alguém já escalado neste evento.',
      couple: `Reveza os eventos com o cônjuge, ${spouse}, que já está neste evento.`,
      trainee: `Está em treinamento em ${role.name} e não há alguém experiente na função para acompanhá-lo(a).`,
      experience: `${role.name} exige ${experienceText(role.minExperience)}.`,
      years: `${role.name} exige ${yearsText(role.minYears)} de casa.`,
    };

    if (block) return say(block, RULE_DETAIL[block]);

    return say('free', 'Nada impede agora: dá para escalar nesta vaga.', { kind: 'assign' });
  });

  return {
    enabledCount: enabled.length,
    when,
    candidates,
    suggestions: suggestionsFor(role, candidates, options, when, outside),
  };
}

function suggestionsFor(
  role: Role,
  candidates: readonly PendingCandidate[],
  options: ExplainOptions,
  when: string,
  outside: ReadonlyMap<string, string>
): string[] {
  const has = (b: PendingBlocker) => candidates.some((c) => c.blocker === b);
  const tips: string[] = [];

  if (candidates.length < 3)
    tips.push(`Habilite mais membros em ${role.name} (Ministérios › ${role.name} › Adicionar).`);
  if (candidates.some((c) => c.action?.kind === 'move'))
    tips.push(
      `Use "Trocar para ${role.name}" em quem já está em outra função deste evento: a vaga passa para a função de onde a pessoa saiu.`
    );
  else if (candidates.some((c) => c.blocker === 'scheduled' && !outside.has(c.memberId)))
    tips.push(
      'Troque à mão quem já está em outra função deste evento, ou habilite mais gente nessa outra função para liberá-lo(a).'
    );

  const kept = [...new Set(outside.values())];

  if (kept.length)
    tips.push(
      `${kept.join(' e ')} ${kept.length > 1 ? 'ficam' : 'fica'} fora desta geração e ninguém sai de lá por ela: salve a escala e, se precisar, combine com quem edita ${kept.length > 1 ? 'essas funções' : 'a função'} a troca no painel do evento.`
    );
  if (has('unavailable')) tips.push(`Confirme com quem marcou indisponível se pode servir ${when}.`);
  if (has('unset'))
    tips.push(
      'Peça aos membros que configurem a disponibilidade, ou ligue "Aceitar quem não configurou" na geração.'
    );
  if (has('limit')) tips.push(`Aumente o limite de ${options.limit} escalas por membro no mês.`);
  if (['pair', 'couple', 'trainee', 'experience', 'years'].some((b) => has(b as PendingBlocker)))
    tips.push(`Revise as regras avançadas de ${role.name} ou a escala do casal no perfil.`);

  tips.push('Ou publique assim e preencha a vaga à mão depois, no painel do evento.');

  return tips;
}

/** O estado com a proposta por cima: a proposta do servidor traz só os eventos gerados. */
export function proposalEvents(
  current: readonly ScheduleEvent[],
  result: Pick<GenerationResult, 'events'>
): ScheduleEvent[] {
  const proposed = new Map(result.events.map((e) => [e.id, e]));
  const known = new Set(current.map((e) => e.id));

  return [...current.map((e) => proposed.get(e.id) ?? e), ...result.events.filter((e) => !known.has(e.id))];
}

export interface ProposalAdjustment {
  eventId: string;
  roleId: string;
  memberId: string;
  fromRoleId: string | null;
}

export type AdjustOutcome = { ok: true; result: GenerationResult } | { ok: false; error: string };

export function adjustProposal(
  result: GenerationResult,
  adjustment: ProposalAdjustment,
  current: readonly ScheduleEvent[],
  members: readonly Member[],
  ministries: readonly Ministry[],
  options: GenerationOptions
): AdjustOutcome {
  const { eventId, roleId, memberId, fromRoleId } = adjustment;
  const pending = result.pending.find((p) => p.eventId === eventId && p.roleId === roleId);

  if (!pending || !result.eventIds.includes(eventId))
    return { ok: false, error: 'Esta vaga não está mais aberta na prévia.' };
  if (current.find((e) => e.id === eventId)?.published)
    return { ok: false, error: 'A escala deste evento já está publicada: troque pelo painel do evento.' };

  const candidate = explainPending(
    pending,
    proposalEvents(current, result),
    members,
    ministries,
    options
  )?.candidates.find((c) => c.memberId === memberId);
  const action = candidate?.action;
  const valid =
    action &&
    (action.kind === 'assign'
      ? fromRoleId === null
      : fromRoleId !== null && action.fromRoleId === fromRoleId);

  if (!valid) return { ok: false, error: 'Esta pessoa não pode ocupar a vaga.' };

  const event = result.events.find((e) => e.id === eventId)!;
  const ministryOfRole = new Map(
    ministries.flatMap((m) => m.roles.map((r) => [r.id, { ministry: m, role: r }] as const))
  );
  const role = ministryOfRole.get(roleId)!.role;
  const slots = { ...event.slots };

  if (fromRoleId) slots[fromRoleId] = (slots[fromRoleId] ?? []).filter((id) => id !== memberId);
  slots[roleId] = [...(slots[roleId] ?? []), memberId];

  const nextEvent: ScheduleEvent = { ...event, slots };
  const events = result.events.map((e) => (e.id === eventId ? nextEvent : e));
  const slotIndex = availIndex(weekdayOf(event.date), periodOf(event.time));
  const member = members.find((m) => m.id === memberId);

  let nextPending = result.pending
    .map((p) => (p === pending ? { ...p, missing: p.missing - 1 } : p))
    .filter((p) => p.missing > 0);
  let { filled, criticalFilled, preferenceHits, couples } = result;
  const load = { ...result.load };
  let decisions = result.decisions;

  if (fromRoleId) {
    const from = ministryOfRole.get(fromRoleId)!;
    const roles = event.ministryIds.flatMap((id) => ministries.find((m) => m.id === id)?.roles ?? []);
    const month = monthKey(event.date);
    const all = proposalEvents(current, { events });
    const loadOf = (id: string) =>
      all.filter((e) => monthKey(e.date) === month && participantsOf(e).has(id)).length;
    const excluded = new Set(options.excludedMemberIds ?? []);
    const enabled = members.filter(
      (m) => isActiveMember(m) && !excluded.has(m.id) && isEnabledIn(m, from.ministry.id, from.role.id)
    );
    const reason = pendingReason(
      enabled,
      new Set(Object.values(slots).flat()),
      { event: nextEvent, role: from.role, roles, experience: experienceIndex(all) },
      slotIndex,
      options,
      (id) => loadOf(id)
    );
    const existing = nextPending.find((p) => p.eventId === eventId && p.roleId === fromRoleId);

    nextPending = existing
      ? nextPending.map((p) => (p === existing ? { ...p, missing: p.missing + 1, reason } : p))
      : [
          ...nextPending,
          {
            eventId,
            roleId: fromRoleId,
            ministryId: from.ministry.id,
            missing: 1,
            reason,
            critical: from.role.critical,
          },
        ];
    criticalFilled += Number(role.critical) - Number(from.role.critical);
    decisions = decisions.filter((d) => !(d.eventId === eventId && d.memberId === memberId));
  } else {
    filled += 1;
    criticalFilled += Number(role.critical);
    load[memberId] = (load[memberId] ?? 0) + 1;
    if (member?.availability[slotIndex] === 'available') preferenceHits += 1;
    if (member && servesTogether(member) && participantsOf(event).has(member.spouseId!)) couples += 1;
  }

  const linked = linkPartners(decisions, events, ministries);
  const order = new Map(
    [...events]
      .filter((e) => result.eventIds.includes(e.id))
      .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time))
      .map((e, i) => [e.id, i])
  );

  return {
    ok: true,
    result: {
      ...result,
      events,
      filled,
      criticalFilled,
      preferenceHits,
      couples,
      pairs: linked.pairs,
      load,
      decisions: linked.decisions,
      pending: [...nextPending].sort((a, b) => (order.get(a.eventId) ?? 0) - (order.get(b.eventId) ?? 0)),
    },
  };
}

export function applyAdjustments(
  result: GenerationResult,
  adjustments: readonly ProposalAdjustment[],
  current: readonly ScheduleEvent[],
  members: readonly Member[],
  ministries: readonly Ministry[],
  options: GenerationOptions
): AdjustOutcome {
  let outcome: AdjustOutcome = { ok: true, result };

  for (const adjustment of adjustments) {
    outcome = adjustProposal(outcome.result, adjustment, current, members, ministries, options);
    if (!outcome.ok) return outcome;
  }

  return outcome;
}
